<template>
  <div ref="wujie" :style="{ height, width }" class="wujie_container" />
</template>

<script>
import { Message } from '@arco-design/web-vue'
import { bus, startApp, destroyApp } from 'wujie'
import loadingSvg from './logo.svg'
import errorImg from '@fe/zxwy-utils/page/v-errorPage/images/404.png'

import router from '@/router'
import { appEnum } from '@/tool/appConfig'

import { usePermissionStore } from '@/store/modules/permission'
import { useUserStore } from '@/store/modules/user'

const defaultProps = {
  useSystemData: () => {
    const permissionStore = usePermissionStore()
    const userStore = useUserStore()

    return {
      authInstance: permissionStore.authInstance,
      userName: userStore.userName,
      deptName: userStore.deptName,
      deptId: userStore.deptId,
      avatar: userStore.avatar,
      userId: userStore.userId,
      sex: userStore.sex,
      phone: userStore.phone
    }
  }
}

bus.$on('print', ()=>{
  const style = `
  @media print {
      html,body,.arco-layout-content,.app_main,.arco-layout,.arco-layout,.layout_container,#app {
        height: auto !important;
        overflow: visible !important;
      }
      .gcontent-box{overflow: visible !important;}
      .arco-layout-sider{
        display: none !important;
      }
    }
  `
  const tag = document.createElement('style')

  tag.innerHTML = style
  document.head.appendChild(tag)
  window.print()
  document.head.removeChild(tag)
})
// 子应用消息提示
bus.$on('message', (type, message) => {
  if (Message[type]) Message[type](message)
})

// 子应用将激活路由同步给主应用，主应用跳转对应路由
bus.$on('routeChange', (fullPath = '', appName, replace = true) => {
  const path = appEnum[appName]?.path || appEnum[appName]?.name || ''

  fullPath = fullPath.replace(/\/$/, '')
  fullPath = `/${path}${fullPath}`.replace(/^\/\//, '/')

  if(appName === 'accounting-system'&&!window.location.pathname?.includes?.('/accounting-system')) {
    return window.open(`${window.location.origin}${fullPath}`)
  }
  if (fullPath === router.currentRoute.value.fullPath) return
  if (replace) router.replace(fullPath)
  else router.push(fullPath)
})

// 前往其他子应用
bus.$on('goApp', (appName, fullPath = '') => {
  if (!appEnum[appName]) return
  bus.$emit('destroy', appName)
  bus.$emit('routeChange', fullPath, appName)
})

// 子应用退出登录
bus.$on('logout', () => {
  usePermissionStore().logout()
})

// 子应用清除缓存
bus.$on('destroy', (appName) => {
  if (appName) destroyApp(appName)
})

// 查看审批
bus.$on('reviewApproval', ({ processInstanceId, taskId, status }) => {
  router.push({
    name: 'ApprovalPage',
    params: { processInstanceId, taskId, status }
  })
})

export default {
  name: 'WujieVue',
  props: {
    width: { type: String, default: '' },
    height: { type: String, default: '' },
    name: { type: String, default: '' },
    loading: { type: HTMLElement, default: null },
    url: { type: String, default: '' },
    path: { type: String, default: '' },
    sync: { type: Boolean, default: false },
    prefix: { type: Object, default: null },
    alive: { type: Boolean, default: false },
    props: { type: Object, default: null },
    fiber: { type: Boolean, default: true },
    degrade: { type: Boolean, default: false },
    attrs: { type: Object, default: null },
    degradeAttrs: { type: Object, default: null },
    replace: { type: Function, default: null },
    fetch: { type: Function, default: null },
    plugins: { type: Array, default: null },
    beforeLoad: { type: Function, default: null },
    beforeMount: { type: Function, default: null },
    afterMount: { type: Function, default: null },
    beforeUnmount: { type: Function, default: null },
    afterUnmount: { type: Function, default: null },
    activated: { type: Function, default: null },
    deactivated: { type: Function, default: null },
    loadError: {type: Function, default: null}
  },
  data() {
    return {
      startAppQueue: Promise.resolve()
    }
  },
  mounted() {
    bus.$onAll(this.handleEmit)
    this.execStartApp()
    this.$watch(
      () => this.name + this.url,
      () => this.execStartApp()
    )
  },
  beforeUnmount() {
    if (!this.alive && !appEnum[this.name]?.alive) destroyApp(this.name)
    bus.$offAll(this.handleEmit)
  },
  methods: {
    handleEmit(event, ...args) {
      this.$emit(event, ...args)
    },
    execStartApp() {
      const loading = document.createElement('div')

      loading.innerHTML = `
        <img src="${loadingSvg}">
        <div class="loading_bar"></div>
      `
      this.startAppQueue = this.startAppQueue.then(async() => {
        try {
          const params = appEnum[this.name] || {}

          await startApp({
            /** 唯一性用户必须保证 */
            name: this.name,
            /** 需要渲染的url */
            url: `${this.url || params.url || ''}${this.path || ''}`,
            /** 渲染的容器 */
            el: this.$refs.wujie,
            /** 子应用加载时loading元素 */
            loading: this.loading || loading,
            /** 路由同步开关， false刷新无效，但是前进后退依然有效 */
            sync: this.sync || params.sync,
            /** 子应用短路径替换，路由同步时生效 */
            prefix: Object.assign({}, this.prefix, params.prefix),
            /** 子应用保活模式，state不会丢失 */
            alive: this.alive || params.alive,
            /** 注入给子应用的数据 */
            props: Object.assign(defaultProps, this.props, params.props),
            /** js采用fiber模式执行 */
            fiber: this.fiber || params.fiber,
            /** 子应用采用降级iframe方案 */
            degrade: this.degrade || params.degrade,
            /** 自定义运行iframe的属性 */
            attrs: Object.assign({}, this.attrs, params.attrs),
            /** 自定义降级渲染iframe的属性 */
            degradeAttrs: Object.assign({}, this.degradeAttrs, params.degradeAttrs),
            /** 代码替换钩子 */
            replace: (codeText) => {
              if (this.replace) codeText = this.replace(codeText)
              if (params.replace) codeText = params.replace(codeText)
              return codeText
            },
            /** 自定义fetch，资源和接口 */
            fetch: this.fetch || params.fetch,
            /** 子应插件 */
            plugins: (this.plugins || []).concat(params.plugins || []),

            /** 子应用生命周期 */
            beforeLoad: (appWindow) => {
              if(this.beforeLoad) this.beforeLoad(appWindow)
              if(params.beforeLoad) params.beforeLoad(appWindow)
            },
            /** 没有做生命周期改造的子应用不会调用 */
            beforeMount: (appWindow) => {
              if(this.beforeMount) this.beforeMount(appWindow)
              if(params.beforeMount) params.beforeMount(appWindow)
            },
            afterMount: (appWindow) => {
              if(this.afterMount) this.afterMount(appWindow)
              if(params.afterMount) params.afterMount(appWindow)
            },
            beforeUnmount: (appWindow) => {
              if(this.beforeUnmount) this.beforeUnmount(appWindow)
              if(params.beforeUnmount) params.beforeUnmount(appWindow)
            },
            afterUnmount: (appWindow) => {
              if(this.afterUnmount) this.afterUnmount(appWindow)
              if(params.afterUnmount) params.afterUnmount(appWindow)
            },
            /** 非保活应用不会调用 */
            activated: (appWindow) => {
              if(this.activated) this.activated(appWindow)
              if(params.activated) params.activated(appWindow)
            },
            deactivated: (appWindow) => {
              if(this.deactivated) this.deactivated(appWindow)
              if(params.deactivated) params.deactivated(appWindow)
            },
            /** 子应用资源加载失败后调用 */
            loadError: (url, err) => {
              if(this.loadError) this.loadError(url, err)
              if(params.loadError) params.loadError(url, err)
              destroyApp(this.name)
              if (this.$el) this.$el.innerHTML = `<div><div><img src="${errorImg}" class="error_img"></div></div>`
            }
          })
        } catch (error) {
          console.error(error)
        }
      })
    }
  }
}

</script>

<style lang="less" scoped>
.wujie_container {
  :deep(.wujie_iframe), :deep(> iframe) {
    display: block;
    height: 100%;
    border: none;
  }
  :deep(> div) {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    > div {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      .error_img { width: 600px; }
      .loading_bar {
        margin: 16px 0 8px;
        width: 140px;
        height: 4px;
        border-radius: 13px;
        background-image: linear-gradient(90deg, #FF6813, #FFFA80, #4EBA56, #FF6813, #FFFA80, #4EBA56);
        background-size: 300% 100%;
        animation: gradient 1.5s linear infinite;
        @keyframes gradient {
          0% {
            background-position: 90% 0%;
          }
          100% {
            background-position: 0% 0%;
          }
        }
      }
    }
  }
}
</style>
